import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MoveHorizontal } from 'lucide-react';
interface ComparisonProps {
  beforeImage: string;
  afterImage: string;
  title: string;
  description: string;
}
const ComparisonSlider = ({
  beforeImage,
  afterImage,
  title,
  description
}: ComparisonProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min(x / rect.width * 100, 100));
    setSliderPosition(percent);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };
  return <div className="mb-16 last:mb-0" data-id="element-45">
      <div className="mb-6" data-id="element-46">
        <h4 className="text-3xl font-serif font-bold text-primary mb-2" data-id="element-47">
          {title}
        </h4>
        <p className="text-gray-600" data-id="element-48">{description}</p>
      </div>

      <div ref={containerRef} className="relative w-full aspect-video rounded-sm overflow-hidden cursor-ew-resize select-none shadow-xl" onMouseMove={onMouseMove} onTouchMove={onTouchMove} onMouseUp={() => setIsDragging(false)} onTouchEnd={() => setIsDragging(false)} onMouseLeave={() => setIsDragging(false)} onMouseDown={() => setIsDragging(true)} onTouchStart={() => setIsDragging(true)} data-id="element-49">
        {/* After Image (Base) */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${afterImage})`
      }} data-id="element-50">
          <div className="absolute top-4 right-4 bg-primary/80 text-white px-3 py-1 text-sm font-medium rounded-sm backdrop-blur-sm" data-id="element-51">
            After
          </div>
        </div>

        {/* Before Image (Clipped) */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${beforeImage})`,
        clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
      }} data-id="element-52">
          <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 text-sm font-medium rounded-sm backdrop-blur-sm" data-id="element-53">
            Before
          </div>
        </div>

        {/* Slider Handle */}
        <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)]" style={{
        left: `${sliderPosition}%`,
        transform: 'translateX(-50%)'
      }} data-id="element-54">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-primary" data-id="element-55">
            <MoveHorizontal size={20} data-id="element-56" />
          </div>
        </div>
      </div>
    </div>;
};
export function BeforeAfter() {
  const projects = [{
    title: 'Modern Kitchen Renovation',
    description: 'Complete transformation of an outdated 90s kitchen into an open, bright, and functional space with a center island.',
    beforeImage: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    afterImage: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
  }, {
    title: 'Master Bathroom Upgrade',
    description: 'Space expansion, walk-in shower installation, and marble finishes for a home spa look.',
    beforeImage: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    afterImage: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
  }];
  return <section className="py-24 bg-light" data-id="element-57">
      <div className="container mx-auto px-6 md:px-12" data-id="element-58">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-59">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-60">
            <h2 className="text-accent font-medium tracking-widest uppercase text-sm mb-3" data-id="element-61">
              Real Results
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6" data-id="element-62">
              Before & After
            </h3>
            <p className="text-gray-600 text-lg" data-id="element-63">
              Drag the slider to see the transformation. Our work speaks for
              itself, showing the difference that experience and attention to
              detail make.
            </p>
          </motion.div>
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-64">
          {projects.map((project, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} data-id="element-65">
              <ComparisonSlider {...project} data-id="element-66" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}